import React, { useState, useEffect } from "react";
import { MapPin, Send, Edit2, Trash2, ExternalLink } from "lucide-react";
import { useParams } from "react-router-dom";
import { adminAPI, venueAPI } from "../../services/api";
import "./Admin.css";

const VenueManager = () => {
  const { eventId } = useParams();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(eventId || "");
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    description: "",
    google_maps_url: "",
    contact_info: "",
  });

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (eventId) {
      setSelectedEventId(eventId);
    }
  }, [eventId]);

  useEffect(() => {
    if (selectedEventId) {
      loadVenues();
    }
  }, [selectedEventId]);

  const loadEvents = async () => {
    try {
      const response = await adminAPI.getEvents();
      setEvents(response.data || []);
    } catch (error) {
      console.error("Failed to load events:", error);
    }
  };

  const loadVenues = async () => {
    setLoading(true);
    try {
      const eventFilter = selectedEventId ? parseInt(selectedEventId) : null;
      const response = await venueAPI.getAll(eventFilter);
      setVenues(response.data || []);
    } catch (error) {
      console.error("Failed to load venues:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEventId) {
      alert("Please select an event");
      return;
    }

    try {
      if (editing) {
        await venueAPI.update(editing, formData);
        alert("Venue updated successfully!");
      } else {
        await venueAPI.create({
          ...formData,
          event_id: parseInt(selectedEventId),
        });
        alert("Venue created successfully!");
      }

      resetForm();
      loadVenues();
    } catch (error) {
      console.error("Failed to save venue:", error);
      alert("Failed to save venue. Please try again.");
    }
  };

  const handleEdit = (venue) => {
    setEditing(venue.id);
    setFormData({
      name: venue.name || "",
      address: venue.address || "",
      description: venue.description || "",
      google_maps_url: venue.google_maps_url || "",
      contact_info: venue.contact_info || "",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this venue?")) return;

    try {
      await venueAPI.delete(id);
      alert("Venue deleted successfully!");
      loadVenues();
    } catch (error) {
      console.error("Failed to delete venue:", error);
      alert("Failed to delete venue.");
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      name: "",
      address: "",
      description: "",
      google_maps_url: "",
      contact_info: "",
    });
  };

  return (
    <div className="admin-page">
      <div className="admin-section-header" style={{ marginBottom: "24px" }}>
        <h1 style={{ fontSize: "1.5rem", fontWeight: "700" }}>
          Venue Management
        </h1>
        <p style={{ color: "var(--admin-text-sub)" }}>
          Manage venue information for events
        </p>
      </div>

      <div className="admin-card" style={{ maxWidth: "800px" }}>
        <div className="card-header">
          <h2>{editing ? "Edit Venue" : "Add New Venue"}</h2>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: "24px" }}>
          {/* Event Selector */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.875rem",
              }}
            >
              Select Event *
            </label>
            <select
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--admin-border)",
                outline: "none",
              }}
            >
              <option value="">-- Select Event --</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </select>
          </div>

          {/* Venue Name */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.875rem",
              }}
            >
              Venue Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Jio World Convention Centre"
              required
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--admin-border)",
                outline: "none",
              }}
            />
          </div>

          {/* Address */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.875rem",
              }}
            >
              Address
            </label>
            <textarea
              rows="2"
              value={formData.address}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
              placeholder="Full address of the venue"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--admin-border)",
                outline: "none",
                resize: "vertical",
              }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.875rem",
              }}
            >
              Description
            </label>
            <textarea
              rows="3"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Describe the venue, facilities, etc."
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--admin-border)",
                outline: "none",
                resize: "vertical",
              }}
            />
          </div>

          {/* Google Maps URL */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.875rem",
              }}
            >
              Google Maps URL
            </label>
            <input
              type="url"
              value={formData.google_maps_url}
              onChange={(e) =>
                setFormData({ ...formData, google_maps_url: e.target.value })
              }
              placeholder="https://maps.google.com/..."
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--admin-border)",
                outline: "none",
              }}
            />
          </div>

          {/* Contact Info */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.875rem",
              }}
            >
              Contact Information
            </label>
            <textarea
              rows="2"
              value={formData.contact_info}
              onChange={(e) =>
                setFormData({ ...formData, contact_info: e.target.value })
              }
              placeholder="Phone, email, or other contact details"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--admin-border)",
                outline: "none",
                resize: "vertical",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              type="submit"
              className="admin-btn btn-primary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
                flex: 1,
              }}
            >
              <Send size={18} />
              {editing ? "Update Venue" : "Create Venue"}
            </button>
            {editing && (
              <button
                type="button"
                onClick={resetForm}
                className="admin-btn"
                style={{ padding: "10px 20px" }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Venues List */}
      <div className="admin-card" style={{ marginTop: "24px" }}>
        <div className="card-header">
          <h2>Venues</h2>
        </div>
        <div style={{ padding: "24px" }}>
          {loading ? (
            <div
              style={{ textAlign: "center", color: "var(--admin-text-sub)" }}
            >
              Loading...
            </div>
          ) : venues.length === 0 ? (
            <div
              style={{ textAlign: "center", color: "var(--admin-text-sub)" }}
            >
              <MapPin
                size={48}
                style={{ opacity: 0.2, marginBottom: "16px" }}
              />
              <p>No venues found for this event.</p>
            </div>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {venues.map((venue) => (
                <div
                  key={venue.id}
                  style={{
                    padding: "16px",
                    background: "#f8fafc",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h3
                        style={{
                          margin: "0 0 8px 0",
                          fontSize: "16px",
                          fontWeight: "600",
                        }}
                      >
                        {venue.name}
                      </h3>
                      {venue.address && (
                        <p
                          style={{
                            margin: "4px 0",
                            fontSize: "13px",
                            color: "#64748b",
                          }}
                        >
                          📍 {venue.address}
                        </p>
                      )}
                      {venue.description && (
                        <p
                          style={{
                            margin: "8px 0",
                            fontSize: "14px",
                            color: "#475569",
                          }}
                        >
                          {venue.description}
                        </p>
                      )}
                      {venue.google_maps_url && (
                        <a
                          href={venue.google_maps_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            marginTop: "8px",
                            fontSize: "13px",
                            color: "#3b82f6",
                          }}
                        >
                          <ExternalLink size={14} />
                          View on Google Maps
                        </a>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => handleEdit(venue)}
                        style={{
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                          padding: "8px",
                        }}
                        title="Edit"
                      >
                        <Edit2 size={16} color="#64748b" />
                      </button>
                      <button
                        onClick={() => handleDelete(venue.id)}
                        style={{
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                          padding: "8px",
                        }}
                        title="Delete"
                      >
                        <Trash2 size={16} color="#ef4444" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VenueManager;
